import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
  EXTERNAL_CALL_URL_MAX,
  LESSON_DURATION_MAX,
  LESSON_DURATION_MIN,
  SERIES_SLOT_MAX,
  externalCallHost,
  localDateAt,
  type EndSeriesResponse,
  type PlanSummary,
  type ScheduleSeriesView,
  type ScheduleSlotInput,
  type ScheduleSlotView,
  type StudentSummary,
} from "@edu/shared";
import * as api from "../../lib/api.ts";
import { lessonCountText } from "../../lib/plurals.ts";
import { useSession } from "../../lib/session.tsx";
import { Crumbs } from "../shell/Crumbs.tsx";
import { CopyButton } from "../shell/CopyLink.tsx";
import { WEEKDAYS, errorText, guessTimeZone, localDateText } from "./common.ts";
import { EndSeriesPanel } from "./EndSeries.tsx";
import { endedText } from "./series-end-text.ts";
import { Loading } from "../../lib/loading.tsx";

/**
 * Экран серии: завести договорённость и править её.
 *
 * ⚠️ Главное требование владельца живёт здесь и выглядит как список строк:
 * **разное время в разные дни это ОДНА серия, а не две.** Вторник 16:00 и
 * четверг 18:30 с одним учеником это две строки слотов с одним `series_id`, и
 * закрыть их обе можно одним действием. Ни `RRULE;BYDAY=TU,TH`, ни календари
 * этого не выражают: там у правила одно время на все дни.
 *
 * Четыре решения экрана:
 *
 * 1. **Серия заводится вместе со слотами, одним запросом.** Серия без слотов
 *    не производит ни одного занятия, и после обрыва сети она осталась бы
 *    пустой строкой в списке, которую никто не опознает
 * 2. **Слот правится НА МЕСТЕ.** Кнопки «удалить день» нет вовсе: `slot_id` это
 *    первая половина ключа занятия, и его исчезновение оставило бы отменённые
 *    занятия висеть в пустоте, а подготовленные уроки без владельца. День
 *    закрывается датой окончания, ровно как серия
 * 3. **Смена дня недели или времени показывает цену заранее.** Сервер
 *    отказывает `409 lessons_orphaned` со списком уроков, которые останутся без
 *    занятия, и правка проходит только вторым нажатием. Тот же приём, что у
 *    диалога расхождения заготовки
 * 4. **Зона принадлежит серии, а не профилю.** Преподаватель, переехавший в
 *    другой город, сам решит, что делать с уже согласованными занятиями:
 *    молчаливый перевод сдвинул бы их за его спиной
 */

interface SlotDraft {
  weekday: number;
  startsAt: string;
}

export function SeriesEditorPage() {
  const { seriesId } = useParams();
  const isNew = seriesId === undefined;

  return isNew ? <NewSeries /> : <EditSeries seriesId={seriesId} />;
}

/* ────────────────────────────── новая серия ──────────────────────────────── */

function NewSeries() {
  const { state } = useSession();
  const navigate = useNavigate();

  const zone = useMemo(() => {
    const mine = state.status === "signed" ? state.me.timeZone : null;
    return mine ?? guessTimeZone();
  }, [state]);

  const [students, setStudents] = useState<StudentSummary[] | null>(null);
  const [studentUserId, setStudentUserId] = useState("");
  const [timeZone, setTimeZone] = useState(zone);
  const [startsOn, setStartsOn] = useState(() => localDateAt(Date.now(), zone));
  const [endsOn, setEndsOn] = useState("");
  const [durationMin, setDurationMin] = useState(60);
  const [lessonTitle, setLessonTitle] = useState("");
  const [note, setNote] = useState("");
  const [defaultPlanId, setDefaultPlanId] = useState("");
  const [externalCallUrl, setExternalCallUrl] = useState("");
  const [slots, setSlots] = useState<SlotDraft[]>([{ weekday: 2, startsAt: "16:00" }]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const res = await api.listStudents();
        // Отвязанному ученику новую договорённость не завести: сервер ответит
        // `student_not_linked`, поэтому его нет и в выборе
        const live = res.students.filter((s) => !s.unlinked);
        setStudents(live);
        setStudentUserId((prev) => (prev === "" ? (live[0]?.userId ?? "") : prev));
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      const created = await api.createSeries({
        studentUserId,
        timeZone,
        startsOn,
        endsOn: endsOn === "" ? null : endsOn,
        durationMin,
        lessonTitle: lessonTitle.trim() === "" ? null : lessonTitle.trim(),
        note: note.trim() === "" ? null : note.trim(),
        defaultPlanId: defaultPlanId === "" ? null : defaultPlanId,
        externalCallUrl: externalCallUrl.trim() === "" ? null : externalCallUrl.trim(),
        slots: slots.map((s) => ({ weekday: s.weekday, startsAt: s.startsAt })),
      });
      void navigate(`/schedule/series/${created.id}`, { replace: true });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  const ready = studentUserId !== "" && slots.length > 0 && startsOn !== "";

  return (
    <main className="home home--form">
      <Head name="Новая серия" />
      {err !== null && <p className="err">{err}</p>}

      <section className="card">
        {students !== null && students.length === 0 ? (
          <p className="muted">
            Сначала заведите ученика: серия это договорённость пары, и без второй стороны её не
            бывает.{" "}
            <Link className="textlink" to="/students">
              К ученикам
            </Link>
            .
          </p>
        ) : (
          <div className="form">
            <label className="field">
              <span className="lbl">Ученик</span>
              <select
                className="fld"
                value={studentUserId}
                onChange={(e) => setStudentUserId(e.target.value)}
                disabled={students === null}
              >
                {students === null && <option value="">Загрузка…</option>}
                {(students ?? []).map((s) => (
                  <option key={s.userId} value={s.userId}>
                    {s.displayName}
                  </option>
                ))}
              </select>
            </label>

            <SeriesFields
              timeZone={timeZone}
              setTimeZone={setTimeZone}
              startsOn={startsOn}
              setStartsOn={setStartsOn}
              endsOn={endsOn}
              setEndsOn={setEndsOn}
              durationMin={durationMin}
              setDurationMin={setDurationMin}
              lessonTitle={lessonTitle}
              setLessonTitle={setLessonTitle}
              note={note}
              setNote={setNote}
              defaultPlanId={defaultPlanId}
              setDefaultPlanId={setDefaultPlanId}
              externalCallUrl={externalCallUrl}
              setExternalCallUrl={setExternalCallUrl}
            />

            <SlotDrafts slots={slots} setSlots={setSlots} />

            <div className="form__foot">
              {/* Ссылка, но по роли это «отмена» из пары: тише сохранения */}
              <Link className="btn btn--quiet" to="/schedule">
                Отмена
              </Link>
              {/* Ради этой кнопки экран и открыт: акцент здесь и больше нигде */}
              <button
                type="button"
                className="btn btn--primary"
                disabled={!ready || busy}
                onClick={() => void submit()}
              >
                {busy ? "Сохраняю…" : "Завести серию"}
              </button>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}

/* ──────────────────────────── правка серии ───────────────────────────────── */

function EditSeries({ seriesId }: { seriesId: string }) {
  const navigate = useNavigate();
  const [series, setSeries] = useState<ScheduleSeriesView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * Завершение закончено: на месте панели строка итога (или ничего), второй
   * круг начинался бы с текста про занятия, которых уже нет. Сбрасывается
   * следующим сохранением на экране, и тогда панель решает по `available`.
   */
  const [ended, setEnded] = useState(false);
  /** Строка итога на месте панели. Только у итога, который панель сама не показывала. */
  const [endedNote, setEndedNote] = useState<string | null>(null);
  /** Серию удалили: перечитывать нечего, ручка ответит 404. */
  const deleted = useRef(false);

  const reload = useCallback(async () => {
    try {
      setSeries(await api.getSeries(seriesId));
      setErr(null);
    } catch (e) {
      setErr(
        e instanceof api.ApiError && e.status === 404
          ? "Серия не найдена"
          : e instanceof Error
            ? e.message
            : String(e),
      );
    }
  }, [seriesId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      // Итог завершения описывал серию до этой правки: после неё он может врать
      // (например, «Окончание» стёрто и занятия вернулись), а панель снова нужна
      setEndedNote(null);
      setEnded(false);
      await reload();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Исполнитель завершения серии: занятость на весь экран, чтобы форма, дни и
   * панель не жали кнопки посреди запроса. Ошибку не ловит, она уходит панели и
   * показывается там, рядом с нажатой кнопкой, а не строкой над экраном.
   *
   * ⚠️ **Серия перечитывается после КАЖДОГО ответа, кроме удаления**, а не по
   * «Готово». Итог с `heldBy` ждёт прочтения в панели, а форма выше в это время
   * держит прежнее пустое «Окончание»: «Сохранить», нажатое до «Готово»,
   * молча возобновило бы только что закрытую серию. Перечитанная серия меняет
   * `key` формы, и поля встают по новому концу.
   */
  async function endExec(fn: () => Promise<void>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      if (!deleted.current) await reload();
    } finally {
      setBusy(false);
    }
  }

  function onEnded(res: EndSeriesResponse) {
    // Серии больше нет: сразу к списку, без перечитывания и мигания 404
    if (res.outcome === "deleted") {
      deleted.current = true;
      void navigate("/schedule/series", { replace: true });
      return;
    }
    setEnded(true);
    // Итог с `heldBy` человек уже прочёл в панели и закрыл «Готово»: второй раз
    // он не нужен. Строка на месте панели только у итога, ушедшего сразу
    if (res.heldBy === null) setEndedNote(endedText(res, Date.now()));
  }

  if (series === null) {
    return (
      <main className="home home--form">
        <Head name={null} />
        {err === null ? <Loading /> : <p className="err">{err}</p>}
      </main>
    );
  }

  const mine = series.myRole === "teacher";

  return (
    <main className="home home--form">
      <Head name={series.counterpartName ?? "Серия"} />
      {err !== null && <p className="err">{err}</p>}

      {series.unlinked && (
        <p className="note">
          Пара отвязана. Прошлые занятия видны, новых не будет, и завести их нельзя, пока ссылка
          ученику не выпущена заново.
        </p>
      )}

      {!mine && (
        <p className="muted">
          Эту серию ведёт ваш преподаватель. Здесь она только для чтения: расписание принадлежит
          тому, кто его ведёт.
        </p>
      )}

      {/*
        ⚠️ `key` по концу серии обязателен: поля формы живут в состоянии первого
        рендера, и без пересоздания после завершения «Сохранить» отправило бы
        прежнее пустое «Окончание», то есть молча возобновило бы серию
      */}
      <SeriesForm
        key={series.endsOn ?? "open"}
        series={series}
        disabled={!mine || busy}
        onSave={run}
      />
      <SlotList series={series} disabled={!mine || busy} onRun={run} />
      {/*
        Завершение у группы своё (роспуск расписания на экране группы), а у
        ученика его нет вовсе: ручка ответит 404
      */}
      {/*
        Итог встаёт на место панели, а не под заголовок: человек смотрит туда,
        где только что нажимал, и строка наверху осталась бы за краем экрана
      */}
      {mine &&
        series.groupId == null &&
        (ended ? (
          endedNote !== null && <p className="note">{endedNote}</p>
        ) : (
          <EndSeriesPanel
            seriesId={series.id}
            available={
              series.endsOn === null || series.endsOn > localDateAt(Date.now(), series.timeZone)
            }
            busy={busy}
            exec={endExec}
            onDone={onEnded}
          />
        ))}
    </main>
  );
}

/** Поля самой серии. Правятся одним сохранением, а не по полю. */
function SeriesForm(props: {
  series: ScheduleSeriesView;
  disabled: boolean;
  onSave: (fn: () => Promise<void>) => Promise<void>;
}) {
  const { series } = props;
  const [timeZone, setTimeZone] = useState(series.timeZone);
  const [startsOn, setStartsOn] = useState(series.startsOn);
  const [endsOn, setEndsOn] = useState(series.endsOn ?? "");
  const [durationMin, setDurationMin] = useState(series.durationMin);
  const [lessonTitle, setLessonTitle] = useState(series.lessonTitle ?? "");
  const [note, setNote] = useState(series.note ?? "");
  const [defaultPlanId, setDefaultPlanId] = useState(series.defaultPlanId ?? "");
  const [externalCallUrl, setExternalCallUrl] = useState(series.externalCallUrl ?? "");

  return (
    <section className="card">
      <header className="card__head">
        <h2>Договорённость</h2>
        <span className="muted">
          {series.counterpartName ?? "без имени"}
          {series.counterpartTimeZone !== null && ` · его зона ${series.counterpartTimeZone}`}
        </span>
      </header>

      <div className="form">
        <SeriesFields
          timeZone={timeZone}
          setTimeZone={setTimeZone}
          startsOn={startsOn}
          setStartsOn={setStartsOn}
          endsOn={endsOn}
          setEndsOn={setEndsOn}
          durationMin={durationMin}
          setDurationMin={setDurationMin}
          lessonTitle={lessonTitle}
          setLessonTitle={setLessonTitle}
          note={note}
          setNote={setNote}
          defaultPlanId={defaultPlanId}
          setDefaultPlanId={setDefaultPlanId}
          externalCallUrl={externalCallUrl}
          setExternalCallUrl={setExternalCallUrl}
        />

        <div className="form__foot">
          {/*
            Единственный акцент экрана правки. Соседних «Сохранить» на экране
            ещё столько же, сколько дней в серии, но те живут в строках списка и
            акцента не получают (правило про карточки): экран открывают ради
            договорённости целиком, а не ради одного её дня
          */}
          <button
            type="button"
            className="btn btn--primary"
            disabled={props.disabled}
            onClick={() =>
              void props.onSave(async () => {
                await api.updateSeries(series.id, {
                  timeZone,
                  startsOn,
                  endsOn: endsOn === "" ? null : endsOn,
                  durationMin,
                  lessonTitle: lessonTitle.trim() === "" ? null : lessonTitle.trim(),
                  note: note.trim() === "" ? null : note.trim(),
                  defaultPlanId: defaultPlanId === "" ? null : defaultPlanId,
                  externalCallUrl:
                    externalCallUrl.trim() === "" ? null : externalCallUrl.trim(),
                });
              })
            }
          >
            Сохранить
          </button>
        </div>
      </div>
    </section>
  );
}

/** Слоты: те самые «разное время в разные дни». */
function SlotList(props: {
  series: ScheduleSeriesView;
  disabled: boolean;
  onRun: (fn: () => Promise<void>) => Promise<void>;
}) {
  const { series } = props;
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<SlotDraft>({ weekday: 2, startsAt: "16:00" });

  return (
    <section className="card">
      <header className="card__head">
        <h2>Дни и время</h2>
        <span className="muted">
          {series.slots.length} из {SERIES_SLOT_MAX}
        </span>
      </header>

      <p className="muted">
        Вторник в 16:00 и четверг в 18:30 это одна серия, а не две. Добавьте столько дней, сколько
        нужно, у каждого своё время.
      </p>

      {/*
        ⚠️ **Стопка карточек, а не `.list`, и это решение** (14.09.2026, долг
        подхода 2г). День серии это не строка со значениями, а ФОРМА: три поля,
        своя кнопка «Сохранить», включающаяся от изменения, и разворачивающийся
        под ней список осиротевших уроков с подтверждением в два нажатия. Слоты у
        таких строк совпадать не могут в принципе: у одной их три, у соседней
        три плюс полэкрана предупреждения. Правило «такая стопка это список»
        сводит однородные строки, а не формы, и путать это нельзя: список,
        собранный из форм, назначает колонку тому, у чего ширина зависит от
        нативного календарика.
      */}
      <ul className="cards">
        {series.slots.map((slot) => (
          <SlotRow
            key={slot.id}
            series={series}
            slot={slot}
            disabled={props.disabled}
            onRun={props.onRun}
          />
        ))}
      </ul>

      {adding ? (
        <div className="form">
          <SlotFields draft={draft} onChange={setDraft} />
          <div className="form__foot">
            <button type="button" className="btn btn--quiet" onClick={() => setAdding(false)}>
              Отмена
            </button>
            <button
              type="button"
              className="btn"
              disabled={props.disabled || series.slots.length >= SERIES_SLOT_MAX}
              onClick={() =>
                void props.onRun(async () => {
                  const body: ScheduleSlotInput = {
                    weekday: draft.weekday,
                    startsAt: draft.startsAt,
                  };
                  await api.addSlot(series.id, body);
                  setAdding(false);
                })
              }
            >
              Добавить
            </button>
          </div>
        </div>
      ) : (
        <p>
          <button
            type="button"
            className="btn"
            disabled={props.disabled || series.slots.length >= SERIES_SLOT_MAX}
            onClick={() => setAdding(true)}
          >
            Добавить день
          </button>
        </p>
      )}
    </section>
  );
}

/**
 * Один слот.
 *
 * ⚠️ Смена дня недели или времени двигает начало действия слота на сегодня, и
 * это не деталь: без сдвига сетка за прошлые месяцы начала бы производить дни,
 * которых не было, а настоящие прошлые уроки выпали бы из неё совсем. Цена
 * названа прямо под полями: прошлое перестаёт выводиться из правила и
 * показывается своими уроками.
 */
function SlotRow(props: {
  series: ScheduleSeriesView;
  slot: ScheduleSlotView;
  disabled: boolean;
  onRun: (fn: () => Promise<void>) => Promise<void>;
}) {
  const { slot, series } = props;
  const [weekday, setWeekday] = useState(slot.weekday);
  const [startsAt, setStartsAt] = useState(slot.startsAt);
  const [endsOn, setEndsOn] = useState(slot.endsOn ?? "");
  /** Список осиротевших уроков, показанный человеку. Второе нажатие с `force`. */
  const [orphans, setOrphans] = useState<{ title: string; occursOn: string }[] | null>(null);

  const changed = weekday !== slot.weekday || startsAt !== slot.startsAt || endsOn !== (slot.endsOn ?? "");
  // Один момент времени на всю строку: год у даты пишется, только когда он не
  // текущий, и решаться это обязано у всех дат строки одинаково
  const now = Date.now();

  async function save(force: boolean) {
    await props.onRun(async () => {
      try {
        await api.updateSlot(series.id, slot.id, {
          weekday,
          startsAt,
          endsOn: endsOn === "" ? null : endsOn,
          ...(force ? { force: true } : {}),
        });
        setOrphans(null);
      } catch (e) {
        if (e instanceof api.ApiError && e.code === "lessons_orphaned") {
          setOrphans(orphanList(e.details));
          return;
        }
        throw e;
      }
    });
  }

  return (
    <li className="card">
      <div className="form form--row">
        <select
          className="fld"
          value={weekday}
          disabled={props.disabled}
          aria-label="День недели"
          onChange={(e) => setWeekday(Number(e.target.value))}
        >
          {WEEKDAYS.map((name, i) => (
            <option key={name} value={i + 1}>
              {name}
            </option>
          ))}
        </select>
        <input
          className="fld"
          type="time"
          value={startsAt}
          disabled={props.disabled}
          aria-label="Время начала"
          onChange={(e) => setStartsAt(e.target.value)}
        />
        <label className="field">
          <span className="lbl">по</span>
          <input
            className="fld"
            type="date"
            value={endsOn}
            disabled={props.disabled}
            onChange={(e) => setEndsOn(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="btn"
          disabled={props.disabled || !changed}
          onClick={() => void save(false)}
        >
          Сохранить
        </button>
      </div>

      {/*
        Даты словами, а не как их прислал сервер: «2026-03-03» это машинная
        форма, и читает её человек, который в этот день ведёт занятие. Функция
        общая с расписанием группы и списком серий (`common.ts`), чтобы на
        соседних экранах не стояли две редакции одной даты
      */}
      {slot.endsOn !== null && (
        <p className="muted">
          День закрыт: занятий после {localDateText(slot.endsOn, now)} по нему не будет.
        </p>
      )}
      {slot.startsOn !== null && (
        <p className="muted">действует с {localDateText(slot.startsOn, now)}</p>
      )}

      {orphans !== null && (
        <div className="note">
          {/*
            ⚠️ Счёт считает `lessonCountText`, а не тернарник на двух формах:
            стояло «подготовленных урока или урока», то есть обе формы разом,
            и при пяти уроках фраза была неправдой в любом прочтении
          */}
          <p>
            Правка снимает с расписания {lessonCountText(orphans.length)}. Урок никуда не денется,
            но останется без своего занятия и будет виден в расписании отдельной строкой:
          </p>
          <ul>
            {orphans.map((o) => (
              <li key={`${o.occursOn}${o.title}`}>
                {localDateText(o.occursOn, now)} · {o.title}
              </li>
            ))}
          </ul>
          <div className="form__foot">
            <button type="button" className="btn btn--quiet" onClick={() => setOrphans(null)}>
              Отмена
            </button>
            {/*
              Заливка красным, а не обводка: список выше уже показан, отступать
              некуда, и следующее нажатие снимает уроки с расписания насовсем.
              Это и есть тот единственный шаг, на котором вес обязан сказать
              «дальше не отменить». Акцента в этом блоке нет вовсе: экран
              спрашивает про необратимое, и синей кнопке рядом с красной места
              нет (акцент экрана живёт выше, в панели договорённости, и в этот
              блок не входит)
            */}
            <button
              type="button"
              className="btn btn--danger is-confirm"
              disabled={props.disabled}
              onClick={() => void save(true)}
            >
              Всё равно поправить
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

/* ──────────────────────────── общие куски формы ──────────────────────────── */

function SeriesFields(props: {
  timeZone: string;
  setTimeZone: (v: string) => void;
  startsOn: string;
  setStartsOn: (v: string) => void;
  endsOn: string;
  setEndsOn: (v: string) => void;
  durationMin: number;
  setDurationMin: (v: number) => void;
  lessonTitle: string;
  setLessonTitle: (v: string) => void;
  note: string;
  setNote: (v: string) => void;
  /** Пустая строка означает «без заготовки». */
  defaultPlanId: string;
  setDefaultPlanId: (v: string) => void;
  /** Пустая строка означает «ссылки на созвон нет». */
  externalCallUrl: string;
  setExternalCallUrl: (v: string) => void;
}) {
  /**
   * Заготовки читаются здесь, а не в двух родителях.
   *
   * Экран серии открывают редко, а список заготовок это десятки строк: лишний
   * запрос дешевле третьей копии загрузки в новой серии и в правке.
   */
  const [plans, setPlans] = useState<PlanSummary[] | null>(null);
  /** Домен набранной ссылки: тот же расчёт, что показывается ученику. */
  const callHost = externalCallHost(props.externalCallUrl.trim());
  /** Набрано, но не разобралось. Пустое поле законно, поэтому не ошибка. */
  const callBad = props.externalCallUrl.trim() !== "" && callHost === null;
  /** Своё имя полю созвона: подпись у него больше не обнимающая, см. ниже. */
  const callFieldId = useId();
  useEffect(() => {
    void (async () => {
      try {
        setPlans((await api.listPlans()).plans);
      } catch {
        // Отказ здесь не должен ронять экран: заготовка это необязательное
        // поле, а всё остальное правится и без неё
        setPlans([]);
      }
    })();
  }, []);

  return (
    <>
      <label className="field">
        <span className="lbl">Часовой пояс серии</span>
        <input
          className="fld"
          value={props.timeZone}
          onChange={(e) => props.setTimeZone(e.target.value)}
        />
      </label>
      <p className="hint">
        Время занятий записано в этой зоне. Она принадлежит серии, а не вашему профилю: смена пояса
        в профиле не двигает уже согласованные занятия.
      </p>

      {/*
        ⚠️ **Подписи ряда стали словами 14.09.2026** (долг подхода 2д). До этого
        дня тут стояли «с», «по» и «минут»: подпись жила СБОКУ от поля, и ряд
        читался фразой «с [дата] по [дата] минут [60]». Общее правило поставило
        подпись сверху (одно положение вместо четырёх), и фраза распалась на три
        столбца, у которых в заголовке предлог. Предлог заголовком не бывает: «с»
        над календариком не называет ничего, а место под себя занимает.

        Поэтому подписи здесь слова, ровно те же, что на артборде формы:
        «Начало», «Окончание», «Длительность». Высота ряда от этого не выросла
        (подпись одна строка на все три поля), а занятое ею место наконец
        что-то говорит. Единица измерения уехала в саму подпись: строкой
        подсказки под одним полем из трёх она развалила бы ряд, потому что
        `.form--row` равняет поля по НИЖНЕМУ краю.
      */}
      <div className="form form--row">
        <label className="field">
          <span className="lbl">Начало</span>
          <input
            className="fld"
            type="date"
            value={props.startsOn}
            onChange={(e) => props.setStartsOn(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="lbl">Окончание</span>
          <input
            className="fld"
            type="date"
            value={props.endsOn}
            onChange={(e) => props.setEndsOn(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="lbl">Длительность, минут</span>
          <input
            className="fld"
            type="number"
            min={LESSON_DURATION_MIN}
            max={LESSON_DURATION_MAX}
            value={props.durationMin}
            onChange={(e) => props.setDurationMin(Number(e.target.value))}
          />
        </label>
      </div>
      <p className="hint">Пустое окончание означает бессрочную серию.</p>

      <label className="field">
        <span className="lbl">Название урока</span>
        <input
          className="fld"
          value={props.lessonTitle}
          placeholder="Занятие"
          onChange={(e) => props.setLessonTitle(e.target.value)}
        />
      </label>

      <label className="field">
        <span className="lbl">Заготовка</span>
        <select
          className="fld"
          value={props.defaultPlanId}
          disabled={plans === null}
          onChange={(e) => props.setDefaultPlanId(e.target.value)}
        >
          <option value="">без заготовки</option>
          {(plans ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.title}
            </option>
          ))}
        </select>
      </label>
      {/*
        Заготовка серии закрывает названный остаток проекта: раньше урок,
        заведённый кнопкой на занятии, оставался без материала навсегда, потому
        что заготовка выбиралась только при создании урока
      */}
      <p className="hint">
        Из неё собираются уроки этой серии. Материал урок берёт при первом открытии доски, поэтому
        правки заготовки доезжают до ещё не открытых занятий сами.
      </p>

      {/*
        Внешний созвон. Режим сосуществования, раздел 7 docs/switching-costs.md:
        продукт обязан работать у того, кто продолжает вести уроки в Zoom или
        Телемосте. Место ссылки на серии, а не на занятии, потому что
        персональная комната Zoom (PMI) у преподавателя постоянная.

        ⚠️ Подпись отвязана от поля через `htmlFor`, а не обнимает его, и это не
        вкусовщина: рядом с полем встала кнопка копирования, а вложенная в
        `<label>` кнопка отдаёт своё нажатие полю. Значок, а не слово: подпись
        поля стоит строкой выше и говорит то же самое (раздел 8б
        `docs/cabinet-tables-design.md`).

        Пока адрес не разобрался, кнопка ПОГАШЕНА, а не убрана: появляясь и
        пропадая по мере набора, она дёргала бы поле ровно в тот миг, когда
        адрес стал разбираться. Копировать полунабранное всё равно нечего.
      */}
      <div className="field">
        <label className="lbl" htmlFor={callFieldId}>
          Ссылка на созвон
        </label>
        <div className="form form--row">
          <input
            className="fld"
            id={callFieldId}
            value={props.externalCallUrl}
            maxLength={EXTERNAL_CALL_URL_MAX}
            inputMode="url"
            placeholder="https://us02web.zoom.us/j/…"
            aria-invalid={callBad}
            onChange={(e) => props.setExternalCallUrl(e.target.value)}
          />
          <CopyButton
            url={props.externalCallUrl.trim()}
            label="Скопировать ссылку на созвон"
            disabled={callHost === null}
          />
        </div>
      </div>
      {/*
        ⚠️ Строка обязательная, а не украшение, и по двум причинам сразу. Первая:
        домен назван вслух, потому что дальше по этой ссылке пойдёт ученик, а
        белого списка доменов у нас нет намеренно. Вторая: ученик видит ссылку
        наравне с кнопкой входа в нашу комнату, и преподаватель обязан это
        знать, прежде чем вписывать сюда адрес

        ⚠️ Ненабравшийся адрес ЗАМЕНЯЕТ эту строку, а не встаёт под ней, и слова
        у отказа те же самые, что у двух других мест с тем же полем (занятие и
        расписание группы). Три поля одного смысла вели себя до 14.09.2026
        по-разному: одно молчало вовсе, второе показывало отказ рядом с
        подсказкой, третье вместо неё
      */}
      {callBad ? (
        <p className="hint hint--err">Нужен полный адрес, начинающийся с https:// и без пробелов</p>
      ) : (
        <p className="hint">
          {callHost !== null && `Ведёт на ${callHost}. `}
          Zoom, Телемост, Meet, что угодно на https. Ученик увидит её рядом с кнопкой входа в нашу
          комнату. Пусто означает, что созвон только у нас.
        </p>
      )}

      <label className="field">
        <span className="lbl">Заметка</span>
        <textarea
          className="fld"
          rows={2}
          value={props.note}
          onChange={(e) => props.setNote(e.target.value)}
        />
      </label>
      {/*
        ⚠️ Строка обязательная, а не украшение. В такие поля преподаватели пишут
        то, чего ученик видеть не должен, и правило видимости стоит на сервере,
        в одном месте сборки ответа. Здесь оно проговаривается человеку
      */}
      <p className="hint">Заметку видите только вы. Ученику она не показывается.</p>
    </>
  );
}

function SlotDrafts(props: { slots: SlotDraft[]; setSlots: (v: SlotDraft[]) => void }) {
  return (
    <>
      <span className="lbl">Дни и время</span>
      <p className="hint">
        В разные дни может быть разное время, и это одна серия. Вторник 16:00 и четверг 18:30
        заводятся двумя строками.
      </p>
      {props.slots.map((slot, i) => (
        <div className="form form--row" key={`${String(i)}${slot.startsAt}`}>
          <SlotFields
            draft={slot}
            onChange={(next) => props.setSlots(props.slots.map((s, j) => (j === i ? next : s)))}
          />
          <button
            type="button"
            className="btn"
            disabled={props.slots.length === 1}
            onClick={() => props.setSlots(props.slots.filter((_, j) => j !== i))}
          >
            Убрать
          </button>
        </div>
      ))}
      <p>
        <button
          type="button"
          className="btn"
          disabled={props.slots.length >= SERIES_SLOT_MAX}
          onClick={() => props.setSlots([...props.slots, { weekday: 4, startsAt: "18:30" }])}
        >
          Ещё день
        </button>
      </p>
    </>
  );
}

function SlotFields(props: { draft: SlotDraft; onChange: (v: SlotDraft) => void }) {
  return (
    <>
      <select
        className="fld"
        value={props.draft.weekday}
        aria-label="День недели"
        onChange={(e) => props.onChange({ ...props.draft, weekday: Number(e.target.value) })}
      >
        {WEEKDAYS.map((name, i) => (
          <option key={name} value={i + 1}>
            {name}
          </option>
        ))}
      </select>
      <input
        className="fld"
        type="time"
        value={props.draft.startsAt}
        aria-label="Время начала"
        onChange={(e) => props.onChange({ ...props.draft, startsAt: e.target.value })}
      />
    </>
  );
}

/**
 * Шапка экрана серии.
 *
 * Место главного действия пустое: у формы главная кнопка стоит в её подвале,
 * рядом с «Отмена», и вторая копия наверху означала бы две главные кнопки на
 * одном экране.
 */
function Head({ name }: { name: string | null }) {
  return (
    <header className="head">
      <Crumbs
        trail={[
          { label: "Расписание", to: "/schedule" },
          { label: "Серии", to: "/schedule/series" },
        ]}
        here={name ?? "Серия"}
      />
      <div className="head__line">
        <h1 className="head__h">{name ?? "Серия"}</h1>
      </div>
      <div className="head__sub">повторяющееся занятие с одним учеником</div>
    </header>
  );
}

/** Список осиротевших уроков из тела отказа. Форма известна серверу и нам. */
function orphanList(details: Readonly<Record<string, unknown>>): { title: string; occursOn: string }[] {
  const raw = details.lessons;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (typeof item !== "object" || item === null) return [];
    const rec = item as { title?: unknown; occursOn?: unknown };
    return typeof rec.title === "string" && typeof rec.occursOn === "string"
      ? [{ title: rec.title, occursOn: rec.occursOn }]
      : [];
  });
}
